import React, { useEffect, useMemo, useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  FaAd,
  FaBars,
  FaBriefcase,
  FaBook,
  FaBookOpen,
  FaBookReader,
  FaBroadcastTower,
  FaCalendarAlt,
  FaChartLine,
  FaChartPie,
  FaCheckCircle,
  FaChevronRight,
  FaChalkboardTeacher,
  FaCommentDots,
  FaClipboard,
  FaCog,
  FaComments,
  FaEnvelopeOpenText,
  FaFileContract,
  FaGift,
  FaHourglassHalf,
  FaImage,
  FaLayerGroup,
  FaLock,
  FaMoneyCheckAlt,
  FaMusic,
  FaNewspaper,
  FaSignOutAlt,
  FaStar,
  FaTv,
  FaUserCog,
  FaUserFriends,
  FaUserShield,
  FaUserTie,
  FaUsers,
  FaWallet,
} from "react-icons/fa";
import { clearPanelSession } from "./utils/panelSession";

const SECTION_ORDER = [
  "Overview",
  "People",
  "Operations",
  "CRM",
  "Media",
  "Finance",
  "System",
];

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const employee = useMemo(() => safeGetEmployee(), []);
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  const role = String(employee?.role || "").toLowerCase();
  const isSuperAdmin = role === "super_admin";
  const isWalletAdminRole = role === "super_admin" || role === "admin";
  const userPerms = Array.isArray(employee?.permissions) ? employee.permissions : [];

  const links = [
    {
      to: "/panel/dashboard",
      icon: <FaLayerGroup />,
      label: "Dashboard",
      section: "Overview",
      perm: "view_dashboard",
      exact: true,
    },
    {
      to: "/panel/users",
      icon: <FaUsers />,
      label: "Users",
      section: "People",
      perm: "view_users",
    },
    {
      to: "/panel/artists",
      icon: <FaUserTie />,
      label: "Artists",
      section: "People",
      perm: "view_artists",
    },
    {
      to: "/panel/mentors",
      icon: <FaChalkboardTeacher />,
      label: "Mentors",
      section: "People",
      perm: "view_mentors",
    },
    {
      to: "/panel/event-managers",
      icon: <FaUserFriends />,
      label: "Event Managers",
      section: "People",
      perm: "view_event_managers",
    },
    {
      to: "/panel/roles",
      icon: <FaUserShield />,
      label: "Roles",
      section: "Operations",
      perm: "view_roles",
    },
    {
      to: "/panel/permission",
      icon: <FaLock />,
      label: "Permission",
      section: "Operations",
      perm: "view_permission",
    },
    {
      to: "/panel/employees",
      icon: <FaUserCog />,
      label: "Team",
      section: "Operations",
      perm: "view_employees",
    },
    {
      to: "/panel/news",
      icon: <FaNewspaper />,
      label: "News Manager",
      section: "Operations",
      perm: "view_news",
    },
    {
      to: "/panel/artist-reports",
      icon: <FaClipboard />,
      label: "Artist Reports",
      section: "Operations",
      perm: "view_artist_reports",
    },
    {
      to: "/panel/all-artist-joining-report",
      icon: <FaChartPie />,
      label: "All Artist Joining Report",
      section: "Operations",
      adminOnly: true,
      permAny: ["view_artists", "view_artist_reports", "manage_artists"],
    },
    {
      to: "/panel/artist-bookings",
      icon: <FaCalendarAlt />,
      label: "Artist Bookings",
      section: "Operations",
      perm: "view_artist_bookings",
    },
    {
      to: "/panel/mentor-bookings",
      icon: <FaBookOpen />,
      label: "Mentor Bookings",
      section: "Operations",
      perm: "view_mentor_bookings",
    },
    {
      to: "/panel/booked-events",
      icon: <FaBook />,
      label: "Booked Events",
      section: "Operations",
      perm: "view_booked_events",
    },
    {
      to: "/panel/event-discovery",
      icon: <FaStar />,
      label: "All Events",
      section: "Operations",
      permAny: [
        "view_event_discovery",
        "manage_event_discovery",
        "feature_events",
        "view_events_venues_approval",
        "approve_events",
        "edit_event_status",
      ],
    },
    {
      to: "/panel/artist-verification",
      icon: <FaCheckCircle />,
      label: "Artist Verification",
      section: "Operations",
      permAny: [
        "view_artist_verification",
        "verify_artist",
        "manage_artists",
        "manage_artist_pro_profiles",
        "moderate_artist_media",
      ],
    },
    {
      to: "/panel/verified-artists",
      icon: <FaStar />,
      label: "Verified Artists",
      section: "Operations",
      permAny: [
        "view_verified_artists",
        "view_artists",
        "manage_artists",
        "manage_artist_pro_profiles",
        "moderate_artist_media",
        "feature_artists",
      ],
    },
    {
      to: "/panel/opportunities",
      icon: <FaBriefcase />,
      label: "Opportunity Board",
      section: "Operations",
      permAny: [
        "view_opportunities",
        "manage_opportunities",
        "moderate_opportunities",
        "feature_opportunities",
      ],
    },
    {
      to: "/panel/opportunities/moderation",
      icon: <FaBriefcase />,
      label: "Opportunity Moderation",
      section: "Operations",
      permAny: [
        "view_opportunities",
        "manage_opportunities",
        "moderate_opportunities",
        "feature_opportunities",
      ],
    },
    {
      to: "/panel/chat-support",
      icon: <FaComments />,
      label: "Chat Support",
      section: "Operations",
      perm: "view_chat_support",
    },
    {
      to: "/panel/chat-support-audit",
      icon: <FaClipboard />,
      label: "Chat Audit",
      section: "Operations",
      perm: "view_chat_support",
    },
    {
      to: "/panel/manage-crm",
      icon: <FaCommentDots />,
      label: "Manage CRM",
      section: "CRM",
      permAny: [
        "view_crm",
        "manage_crm",
        "manage_crm_contacts",
        "manage_crm_templates",
        "send_crm_broadcast",
        "view_crm_reports",
      ],
    },
    {
      to: "/panel/mail-apnaartist",
      icon: <FaEnvelopeOpenText />,
      label: "Mail ApnaArtist",
      section: "CRM",
      permAny: [
        "view_crm",
        "manage_crm",
        "manage_crm_contacts",
        "manage_crm_templates",
        "send_crm_broadcast",
        "view_crm_reports",
      ],
    },
    {
      to: "/panel/post-in-app",
      icon: <FaComments />,
      label: "POST IN APP",
      section: "Media",
      permAny: [
        "view_posts",
        "view_story",
        "view_opportunities",
        "view_artist_bookings",
        "view_mentor_bookings",
        "view_song_request",
        "view_booked_events",
      ],
    },
    {
      to: "/panel/posts",
      icon: <FaClipboard />,
      label: "All Posts",
      section: "Media",
      perm: "view_posts",
    },
    {
      to: "/panel/story",
      icon: <FaBookReader />,
      label: "All Stories",
      section: "Media",
      perm: "view_story",
    },
    {
      to: "/panel/song-request",
      icon: <FaMusic />,
      label: "Song Request",
      section: "Media",
      perm: "view_song_request",
    },
    {
      to: "/panel/broadcast",
      icon: <FaBroadcastTower />,
      label: "Broadcast",
      section: "Media",
      perm: "view_broadcast",
    },
    {
      to: "/panel/app-adds",
      icon: <FaAd />,
      label: "Run Ads",
      section: "Media",
      perm: "view_app_adds",
    },
    {
      to: "/panel/app_media_monitor",
      icon: <FaChartLine />,
      label: "Media Monitor",
      section: "Media",
      perm: "view_media_monitor",
    },
    {
      to: "/panel/app_event_banner",
      icon: <FaImage />,
      label: "App Event Banner",
      section: "Media",
      permAny: ["view_event_app_banners", "manage_event_app_banners"],
    },
    {
      to: "/panel/app_home_banner",
      icon: <FaImage />,
      label: "App Home Banner",
      section: "Media",
      permAny: ["view_event_app_banners", "manage_event_app_banners"],
    },
    {
      to: "/panel/tv-display",
      icon: <FaTv />,
      label: "TV Display",
      section: "Media",
      permAny: [
        "view_settings",
        "manage_settings",
        "view_dashboard",
      ],
      adminOnly: true,
    },
    {
      to: "/panel/create_rewards_and_offer",
      icon: <FaGift />,
      label: "Rewards & Offers",
      section: "Media",
    },
    {
      to: "/panel/wallets",
      icon: <FaWallet />,
      label: "Manage Wallets",
      section: "Finance",
      walletOnly: true,
    },
    {
      to: "/panel/withdraw-requests",
      icon: <FaMoneyCheckAlt />,
      label: "Withdraw Requests",
      section: "Finance",
      walletOnly: true,
    },
    {
      to: "/panel/wallet-audit",
      icon: <FaClipboard />,
      label: "Wallet Audit",
      section: "Finance",
      walletOnly: true,
    },
    {
      to: "/panel/overtime-monitoring",
      icon: <FaHourglassHalf />,
      label: "Overtime Monitor",
      section: "Finance",
      permAny: ["view_overtime_monitoring", "manage_overtime_monitoring", "wallet_admin"],
    },
    {
      to: "/panel/settings",
      icon: <FaCog />,
      label: "Settings",
      section: "System",
      permAny: [
        "view_settings",
        "manage_settings",
        "manage_payment_settings",
        "manage_api_settings",
        "manage_authkey_settings",
      ],
    },
    {
      to: "/panel/handle-post-story-reel",
      icon: <FaLayerGroup />,
      label: "Handle Post/Story/Reel",
      section: "System",
      permAny: [
        "view_settings",
        "manage_settings",
      ],
    },
    {
      to: "/panel/legal-agreement",
      icon: <FaFileContract />,
      label: "Leagl&AGREMENT",
      section: "System",
    },
  ];

  const canSee = (link) => {
    if (link.walletOnly) return isWalletAdminRole;
    if (isSuperAdmin) return true;
    if (link.adminOnly && role === "admin") return true;
    if (!link.perm && !link.permAny) return true;
    if (link.perm) return userPerms.includes(link.perm);
    if (Array.isArray(link.permAny)) return link.permAny.some((perm) => userPerms.includes(perm));
    return false;
  };

  const visibleLinks = links.filter(canSee);

  const navSections = SECTION_ORDER
    .map((section) => ({
      title: section,
      items: visibleLinks.filter((link) => link.section === section),
    }))
    .filter((section) => section.items.length > 0);

  const activeLink =
    [...visibleLinks]
      .sort((a, b) => b.to.length - a.to.length)
      .find((link) => location.pathname === link.to || location.pathname.startsWith(`${link.to}/`)) ||
    visibleLinks[0] ||
    { label: "Admin Panel", section: "Overview" };
  const isDashboardRoute =
    location.pathname === "/panel" ||
    location.pathname === "/panel/dashboard" ||
    location.pathname.startsWith("/panel/dashboard/");

  const handleLogout = async () => {
    await clearPanelSession();
    navigate("/", { replace: true });
  };

  const fullName = employee?.fullName || employee?.full_name || employee?.name || "Admin";
  const roleLabel = prettyRole(employee?.role || "employee");

  return (
    <div className={`aa-shell ${navOpen ? "is-nav-open" : ""}`}>
      <div className="aa-shell-orb aa-shell-orb-a" />
      <div className="aa-shell-orb aa-shell-orb-b" />
      <div className="aa-shell-orb aa-shell-orb-c" />

      <button
        type="button"
        className="aa-mobile-toggle"
        onClick={() => setNavOpen((value) => !value)}
        aria-label={navOpen ? "Close navigation" : "Open navigation"}
      >
        <FaBars />
      </button>

      <button
        type="button"
        className={`aa-sidebar-overlay ${navOpen ? "is-open" : ""}`}
        onClick={() => setNavOpen(false)}
        aria-label="Close navigation overlay"
      />

      <aside className={`aa-sidebar ${navOpen ? "is-open" : ""}`}>
        <div className="aa-sidebar-scroll">
          <div className="aa-brand-card">
            <div className="aa-brand-mark">
              <FaStar />
            </div>
            <div>
              <div className="aa-brand-name">ApnaArtist</div>
              <div className="aa-brand-sub">Admin Control Room</div>
            </div>
          </div>

          <nav className="aa-nav">
            {navSections.map((section) => (
              <section key={section.title} className="aa-nav-section">
                <div className="aa-nav-title">{section.title}</div>
                <div className="aa-nav-items">
                  {section.items.map((link) => (
                    <NavLink
                      key={link.to}
                      to={link.to}
                      end={Boolean(link.exact)}
                      className={({ isActive }) =>
                        `aa-nav-link ${isActive ? "active" : ""}`
                      }
                    >
                      <span className="aa-nav-icon">{link.icon}</span>
                      <span className="aa-nav-label">{link.label}</span>
                    </NavLink>
                  ))}
                </div>
              </section>
            ))}
          </nav>
        </div>

        <div className="aa-sidebar-footer">
          <button type="button" className="aa-logout-btn" onClick={handleLogout}>
            <FaSignOutAlt />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <div className="aa-main">
        <header className="aa-topbar">
          <div className="aa-topbar-copy">
            <div className="aa-breadcrumb">
              <span>ApnaArtist</span>
              <FaChevronRight />
              <span>{activeLink.section}</span>
              <FaChevronRight />
              <span>{activeLink.label}</span>
            </div>
            <h1 className="aa-topbar-title">{activeLink.label}</h1>
            <p className="aa-topbar-sub">
              Premium brand-first admin workflow with fast actions, cleaner hierarchy, and stronger visual focus.
            </p>
          </div>

          <div className="aa-topbar-actions">
            <div className="aa-status-pill">
              <span className="aa-status-label">Signed In</span>
              <strong>{fullName}</strong>
            </div>
            <div className="aa-status-pill aa-status-pill-brand">
              <span className="aa-live-dot" />
              <strong>{roleLabel}</strong>
            </div>
          </div>
        </header>

        <main className="aa-page-body">
          <div className={`aa-page-stage ${isDashboardRoute ? "aa-page-stage-dashboard" : "aa-page-stage-pro"}`}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function prettyRole(role = "") {
  const value = String(role || "").replace(/_/g, " ").trim();
  if (!value) return "Employee";
  return value.replace(/\b\w/g, (match) => match.toUpperCase());
}

function safeGetEmployee() {
  try {
    const raw = localStorage.getItem("employee");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}
